import { ref, watch } from 'vue'
import { defineStore } from 'pinia'
import type { Role } from '@/utils/permission'

export interface User {
  id: number
  name: string
  role: Role
  user_name?: string
  person_id?: string
  avatar?: string
  mobile?: string
  dingtalk_user_id?: string
  status?: number
}

export interface Customer {
  id: number
  name: string
  code?: string
  category?: string
  status?: number
}

interface AuthStorage {
  token: string | null
  user: User | null
  customers: Customer[]
}

const STORAGE_KEY = 'auth-storage'

function loadFromStorage(): AuthStorage {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      return JSON.parse(stored)
    }
  } catch {}
  return { token: null, user: null, customers: [] }
}

export const useAuthStore = defineStore('auth', () => {
  const initial = loadFromStorage()

  const token = ref<string | null>(initial.token)
  const user = ref<User | null>(initial.user)
  const customers = ref<Customer[]>(initial.customers)

  function setAuth(newToken: string, newUser: User, newCustomers: Customer[]) {
    token.value = newToken
    user.value = newUser
    customers.value = newCustomers
  }

  function clearAuth() {
    token.value = null
    user.value = null
    customers.value = []
  }

  function updateUser(userData: Partial<User>) {
    if (user.value) {
      user.value = { ...user.value, ...userData }
    }
  }

  watch(
    [token, user, customers],
    () => {
      const data: AuthStorage = {
        token: token.value,
        user: user.value,
        customers: customers.value
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
    },
    { deep: true }
  )

  return {
    token,
    user,
    customers,
    setAuth,
    clearAuth,
    updateUser
  }
})
